import { useEffect, useRef, useState } from 'react'
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb'

import { Button } from 'components/Button/Button'
import { Card } from '@cmsgov/ds-medicare-gov'
import { ContentLayout } from 'components/Layouts'
import { medicareApiClient } from 'clients/medicareApiClient'
import { CardList } from 'components/CardList/CardList'
import { convertRepToCardListProp } from 'containers/AccountSettings/repToCardConvert'
import { Divider } from 'components/Divider/Divider'
import { addIsPastRepField, IAuthRepsActionWithPastRepFlag } from '../isPastRep'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getModalType, setModal } from 'redux/reducers/modal'
import { IconInfo } from 'components/Icon/IconInfo'
import { AuthRepPrintDrawer } from 'components/MyDrawer/drawers'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { MyInternalLink, MyLinkSiteActivityProps } from 'components/MyLink'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Component } from '@cms-max/medicare-api-sdk'

export interface AuthRepProps {
}

export const AuthRep: React.FC<AuthRepProps> = () => {
  const [reps, setReps] = useState([])
  const [pastReps, setPastReps] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation('authRep')

  const modalTypeOpen = useSelector(getModalType)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        const reps = await medicareApiClient.asyncGetRepsByReqToken()
        const repsWithPastFlag = addIsPastRepField(reps)

        setLoaded(true)
        setLoading(false)
        setReps(repsWithPastFlag.filter((rep : IAuthRepsActionWithPastRepFlag) => {
          if (!rep.isPastRep) return true
          else return false
        }))
        setPastReps(repsWithPastFlag.filter((rep : IAuthRepsActionWithPastRepFlag) => {
          if (rep.isPastRep) return true
          else return false
        }))
      } catch (error) {
        console.log('error getting reps')
        setLoading(false)
      }
    }
    if (!loaded) { fetchData() }
  }, [reps, pastReps, loaded])

  const dispatch = useDispatch()

  const openPrintableFormDrawer = () => {
    if (modalTypeOpen === 'auth-print') {
      dispatch(setModal({ modalType: undefined }))
    } else {
      dispatch(setModal({ modalType: 'auth-print' }))
    }
  }

  const openPrintDrawerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const addRepSiteActivityProps: MyLinkSiteActivityProps = {
    clickData: {
      linkText: t('authRepView.addButton', { lng: 'en' }),
      linkUrl: buildAbsoluteMedicareMyUrl('/account-settings/auth-rep/new')
    },
    component: Component.AuthorizedRepresentative
  }

  return (
    <>
      <BreadCrumb
        backButtonText={t('navigation.backToSettings')}
        backButtonLink='/account-settings'
        navAriaLabel={t('navigation.backToAccountSettingsMainPageAriaLabel')}
        width='wide'
        siteActivityProps={{
          component: Component.AuthorizedRepresentative,
          clickData: {
            linkText: t('navigation.backToSettings', { lng: 'en' }),
            linkUrl: buildAbsoluteMedicareMyUrl('/account-settings')
          }
        }}
      />
      <ContentLayout heading={t('authRepView.My representatives')} width='wide'>
        {modalTypeOpen === 'auth-print' && (
          <AuthRepPrintDrawer onClose={openPrintableFormDrawer} />
        )}
        {loading
          ? <div className='ds-l-container'>
              <div className='ds-l-row ds-u-justify-content--left'>
                <MySpinner size='large'/>
              </div>
            </div>
          : <div className='ds-l-container'>
              <div className='ds-u-justify-content--center'>
                <h2 className='ds-text-heading--xl my-u-mb--6 ds-u-margin-bottom--2'>{t('authRepView.Add a representative')}</h2>
                <p className='my-u-mb--10'>
                  <span className='my-u-mr--3'> {t('authRepView.heading.text')}</span>
                  <Button
                    className='ds-c-button ds-c-button--ghost ds-u-padding--0 modal-blue my-u-font-size--16 ds-u-text-align--left'
                    ref={openPrintDrawerRef}
                    onClick={openPrintableFormDrawer}
                    siteActivityProps={{
                      component: Component.AuthorizedRepresentative,
                      clickData: {
                        linkText: t('authRepView.heading.link', { lng: 'en' })
                      }
                    }}
                  >
                    {t('authRepView.heading.link')}
                    <div className='ds-u-display--inline-block'>
                      <IconInfo className='my-c-my-svg-icon--primary-alt'/>
                    </div>
                  </Button>
                </p>
                {/* eslint-disable-next-line operator-linebreak */}
                {(reps.length > 0 || pastReps.length > 0) ?
                  <>
                    <MyInternalLink
                      className='ds-c-button ds-c-button--solid'
                      to='/account-settings/auth-rep/new'
                      data-testid='step1-alt'
                      siteActivityProps={addRepSiteActivityProps}
                    >
                      {t('authRepView.addButton')}
                    </MyInternalLink>

                    {/* eslint-disable-next-line operator-linebreak */}
                  </> :
                  <>
                    <Card className='ds-l-lg-col--8 my-u-p--10'>
                      <p>{t('authRepView.paragraph.add')}</p>
                      <MyInternalLink
                        className='ds-c-button ds-c-button--solid'
                        to='/account-settings/auth-rep/new'
                        data-testid='step1-alt'
                        siteActivityProps={addRepSiteActivityProps}
                      >
                        {t('authRepView.addButton')}
                      </MyInternalLink>
                    </Card>
                  </>}
                <Divider className='my-u-my--20' />

                {(reps.length > 0 || pastReps.length > 0) &&
                  <>
                    <div className='ds-l-row'>
                      <div className='ds-l-md-col--12 ds-l-lg-col--4'>
                        <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>{t('authRepView.heading.current')}</h2>
                        <p>{t('authRepView.paragraph.current')}</p>
                      </div>
                      <div className='ds-l-md-col--12 ds-l-lg-col--8'>
                        <CardList cardPropsList={reps.map((rep : IAuthRepsActionWithPastRepFlag) => convertRepToCardListProp(rep, t))} emptyContent={<p className='my-u-m--0'>You have no current representatives at this time.</p>} />
                      </div>
                    </div>
                    <Divider className='my-u-my--20' />
                    <div className='ds-l-row'>
                      <div className='ds-l-md-col--12 ds-l-lg-col--4'>
                        <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>{t('authRepView.heading.past')}</h2>
                        <p>{t('authRepView.paragraph.past')}</p>
                      </div>
                      <div className='ds-l-md-col--12 ds-l-lg-col--8'>
                        <CardList cardPropsList={pastReps.map((rep : IAuthRepsActionWithPastRepFlag) => convertRepToCardListProp(rep, t))} hideEdit emptyContent={<p className='my-u-m--0'>You have no past representatives at this time.</p>} />
                      </div>
                    </div>
                    <Divider className='my-u-my--20' />
                  </>}
                <div className='my-u-container--8 my-u-py--6'>
                  <h2 className='ds-text-heading--xl my-u-mt--0 my-u-mb--5'>{t('authRepView.heading.help')}</h2>
                  <p className='my-u-mb--20'>
                    {t('authRepView.paragraph.help')}
                  </p>
                </div>
              </div>
            </div>}
      </ContentLayout>
    </>
  )
}
